<script setup lang="ts">
import { INQUIRY_ITEM } from '@/types'
import { TransitionRoot } from '@headlessui/vue'
import {
  getInitialInquiryStatus,
  sendChangedInquiryStatusEvent,
  sendDeletedInquiryEvent,
} from '@/utils/mixpanel'

const props = defineProps({
  show: Boolean,
  inquiry: {
    type: Object as () => INQUIRY_ITEM,
    required: true,
  },
})

const emits = defineEmits(['close'])

const inquiriesStore = useInquiriesStore()
const route = useRoute()

const currentInquiryStatus = computed(
  () =>
    props.inquiry.status ||
    props.inquiry.pick_up_order_status ||
    props.inquiry.delivery_order_status,
)

const showDeleteConfirmation = ref(false)
const isDeleted = ref(false)

watch(props, (value) => {
  if (value.show) {
    document.getElementsByTagName('html')[0].style.overflowY = 'hidden'
  }
})

const handleClose = () => {
  document.getElementsByTagName('html')[0].style.overflowY = 'auto'
  emits('close')
}

const handleCloseDeleteConfirmation = () => {
  showDeleteConfirmation.value = false
  setTimeout(() => {
    document.getElementsByTagName('html')[0].style.overflowY = 'hidden'
  }, 200)
}

const handleDeleteInquiry = async () => {
  isDeleted.value = true

  await inquiriesStore.deleteInquiry(props.inquiry.id)

  sendDeletedInquiryEvent(props.inquiry, 'Preview')

  setTimeout(() => {
    handleClose()
    showDeleteConfirmation.value = false
  }, 1000)

  setTimeout(() => {
    isDeleted.value = false
  }, 1200)
}

const isChangingStatusEnabled = computed(() => {
  if (!props.inquiry.payment_status) {
    return true
  }

  return !(
    props.inquiry.payment_status &&
    (props.inquiry.delivery_order_status === 'rejected' ||
      props.inquiry.pick_up_order_status === 'rejected')
  )
})

const handleSelectedStatus = (status: INQUIRY_ITEM['status']) => {
  let inquiry = props.inquiry
  const oldStatus = getInitialInquiryStatus(inquiry)!

  if (inquiry.is_order) {
    return
  }

  if (inquiry.status) {
    inquiry = { ...props.inquiry, status }
  } else if (inquiry.pick_up_order_status) {
    inquiry = { ...props.inquiry, pick_up_order_status: status }
  } else if (inquiry.delivery_order_status) {
    inquiry = { ...props.inquiry, delivery_order_status: status }
  }

  sendChangedInquiryStatusEvent(inquiry, oldStatus, route.name as string)
  inquiriesStore.updateInquiry(inquiry)
}
onMounted(() => {
  document.getElementsByTagName('html')[0].style.overflowY = 'hidden'
})
</script>

<template>
  <TransitionRoot
    :show="props.show"
    enter="transition-opacity"
    enter-from="opacity-0"
    enter-to="opacity-100"
    leave="transition-opacity duration-200"
    leave-from="opacity-100"
    leave-to="opacity-0"
  >
    <div
      class="fixed inset-0 flex w-full flex-col items-center overflow-y-hidden px-4 py-6 sm:px-0"
    >
      <!-- Backdrop -->
      <HuiTransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 flex transform transition-all"
          @click="handleClose"
        >
          <div class="absolute inset-0 bg-brand-dark opacity-75"></div>
        </div>
      </HuiTransitionChild>

      <div
        class="mb-6 mt-[5vh] min-w-full transform bg-transparent transition-all"
      >
        <div class="relative rounded-xl p-0">
          <InquiryListItem class="rounded-xl bg-white" v-bind="props.inquiry" />
          <!-- Quick actions -->
          <div class="mt-3 flex justify-center gap-10">
            <div class="flex flex-col items-center justify-center">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20"
                @click="showDeleteConfirmation = true"
              >
                <AppiconTrashCan class="h-5 w-5 fill-white" />
              </div>
              <span class="text-sm text-white">Delete Inquiry</span>
            </div>
            <InquiryStatusBtn
              v-if="isChangingStatusEnabled"
              as="quickActionButton"
              :inquiry-id="Number(props.inquiry.id)"
              :selected-status="currentInquiryStatus"
              @update:status="handleSelectedStatus"
            />
            <div
              v-else
              class="flex flex-col items-center justify-center opacity-30"
            >
              <div
                class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20"
              >
                <AppiconClock class="h-5 w-5 fill-white" />
              </div>
              <span class="text-sm text-white">Change Status</span>
            </div>
          </div>
        </div>
      </div>
      <AppDialog
        :is-open="showDeleteConfirmation"
        :can-close="true"
        :has-close-button="false"
        @close="handleCloseDeleteConfirmation"
      >
        <template #title>
          <div v-if="!isDeleted" class="text-center">
            <p class="text-sm font-semibold text-brand-black">
              Are you sure you want to delete this inquiry?
            </p>
          </div>
        </template>
        <template #content>
          <TransitionRoot
            :show="!isDeleted"
            enter="transition-opacity duration-75"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="transition-opacity duration-200 absolute"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="text-brand-brand-black px-4 pb-4 text-center">
              <p>
                You will permanently lose all data associated with this inquiry.
                We only recommend deleting inquiries if they are duplicates or
                bogus.
              </p>

              <div class="flex gap-4">
                <AppButton
                  class="mt-4 !ring-0"
                  variant="neutral"
                  size="md"
                  :is-rounded="true"
                  :is-wide="true"
                  @click="handleCloseDeleteConfirmation"
                >
                  No
                </AppButton>
                <AppButton
                  tabindex="-1"
                  class="mt-4"
                  size="md"
                  variant="success_yes"
                  :is-rounded="true"
                  :is-wide="true"
                  @click="handleDeleteInquiry"
                >
                  Yes
                </AppButton>
              </div>
            </div>
          </TransitionRoot>

          <TransitionRoot
            :show="isDeleted"
            enter="transition-opacity duration-1000"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="transition-opacity duration-150"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div
              class="flex w-full flex-col items-center justify-center px-4 pb-4 pt-0"
            >
              <div
                class="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100"
              >
                <AppiconTrashCan class="h-5 w-5 fill-slate-800" />
              </div>
              <p class="text-slate-800">Inquiry Deleted!</p>
            </div>
          </TransitionRoot>
        </template>
      </AppDialog>
    </div>
  </TransitionRoot>
  <DoorDashDialogs
    v-if="inquiry.is_spothopper_delivery"
    :selected-inquiry="inquiry"
  />
  <OrderDialogs
    v-if="inquiry.is_order && !inquiry.is_spothopper_delivery"
    :selected-inquiry="inquiry"
  />
</template>
